import { useState } from 'react';

import { Row } from '@tanstack/react-table';
import { NotificationRule } from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { Button } from '@/components/ui/button';
import { LoaderIcon, TrashIcon } from 'lucide-react';
import {
  Credenza,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { useDeleteNotificationRulesMutation } from '@/features/notification-rules/hooks/mutations/use-delete-notification-rules.ts';
import { useUnsubscribeNotificationRule } from '@/features/notification-rules/hooks/mutations/use-unsubscribe-notification-rule.ts';
import { hasPermission } from '@/features/users/schemas/users.permissions.ts';
import { useSession } from '@/services/providers/use-session.ts';

export function NotificationRulesTableToolbarDeleteDialog({
  notificationRules,
  onSuccess,
  showTrigger = true,
}: {
  notificationRules: Row<NotificationRule>['original'][];
  onSuccess?: () => void;
  showTrigger?: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { userId, role } = useSession();
  const isAdmin = hasPermission(
    { userId, role },
    'notification-rules',
    'update.access-management'
  );

  const selfManagedRules = notificationRules.filter((r) => r.managedBy !== 'company-managed');
  const companyManagedRules = notificationRules.filter(
    (r) => r.managedBy === 'company-managed'
  );

  const { mutate: deleteNotificationRuleMutation, isPending: isDeletePending } =
    useDeleteNotificationRulesMutation({
      onSuccess: () => {
        if (isAdmin || companyManagedRules.length === 0) {
          onSuccess?.();
          setIsDialogOpen(false);
        }
      },
    });

  const { mutate: unsubscribe, isPending: isUnsubscribePending } =
    useUnsubscribeNotificationRule({
      onSuccess: () => {
        onSuccess?.();
        setIsDialogOpen(false);
      },
      onError: () => {
        setIsDialogOpen(false);
      },
    });

  const isPending = isDeletePending || isUnsubscribePending;

  const onClickConfirm = () => {
    if (isAdmin) {
      deleteNotificationRuleMutation({
        notificationRuleIds: notificationRules.map((n) => n.notificationRuleId),
      });
    } else {
      if (selfManagedRules.length > 0) {
        deleteNotificationRuleMutation({
          notificationRuleIds: selfManagedRules.map((n) => n.notificationRuleId),
        });
      }

      for (const rule of companyManagedRules) {
        unsubscribe({ ruleId: rule.notificationRuleId });
      }

      if (selfManagedRules.length === 0) {
        onSuccess?.();
        setIsDialogOpen(false);
      }
    }
  };

  const getDescription = () => {
    if (isAdmin || companyManagedRules.length === 0) {
      return (
        <>
          Are you sure you want to delete{' '}
          <span className="font-medium">
            {notificationRules.map((n) => n.notificationTitle).join(', ')}
          </span>
          {notificationRules.length === 1 ? ' notification rule' : ' notification rules'}? This
          action cannot be undone.
        </>
      );
    }

    if (selfManagedRules.length === 0) {
      return (
        <>
          You will be unsubscribed from{' '}
          <span className="font-medium">
            {companyManagedRules.length} company rule
            {companyManagedRules.length !== 1 ? 's' : ''}
          </span>
          .
        </>
      );
    }

    return (
      <>
        <span className="font-medium">
          {selfManagedRules.length} rule{selfManagedRules.length !== 1 ? 's' : ''}
        </span>{' '}
        will be permanently deleted. You will be unsubscribed from{' '}
        <span className="font-medium">
          {companyManagedRules.length} company rule
          {companyManagedRules.length !== 1 ? 's' : ''}
        </span>
        .
      </>
    );
  };

  const confirmButtonLabel =
    !isAdmin && companyManagedRules.length > 0 && selfManagedRules.length === 0
      ? 'Unsubscribe'
      : 'Delete';

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      {showTrigger ? (
        <CredenzaTrigger asChild>
          <Button variant="outline" size="sm" className="flex w-min gap-2">
            <TrashIcon className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline-block">Delete ({notificationRules.length})</span>
          </Button>
        </CredenzaTrigger>
      ) : null}
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>Confirm Notification Rules Deletion</CredenzaTitle>
          <CredenzaDescription>{getDescription()}</CredenzaDescription>
        </CredenzaHeader>
        <CredenzaFooter className="gap-2 sm:space-x-0">
          <CredenzaClose asChild>
            <Button variant="outline">Cancel</Button>
          </CredenzaClose>
          <Button
            aria-label="Delete selected rows"
            variant="destructive"
            onClick={onClickConfirm}
            disabled={isPending}
          >
            {isPending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            {confirmButtonLabel}
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
}

export default NotificationRulesTableToolbarDeleteDialog;
